import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircleHelp, Search } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import {
  NAV_SECTIONS,
  flattenVisibleNavItems,
  type FlatNavCommand,
} from './navConfig'
import { helpPath, type HelpArticleSummary } from '../../lib/help'

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onChooseCompanyRole?: () => void
  onToggleTheme?: () => void
  /** The help articles the caller may read: a "Help" group. Empty or left out when the server has no help. */
  helpArticles?: HelpArticleSummary[]
}

type PaletteRow =
  | { kind: 'nav'; item: FlatNavCommand }
  | { kind: 'action'; id: string; label: string; run: () => void }
  | { kind: 'help'; slug: string; title: string }

export const HELP_GROUP = 'Help'

export default function CommandPalette({
  open,
  onOpenChange,
  onChooseCompanyRole,
  onToggleTheme,
  helpArticles,
}: CommandPaletteProps) {
  const navigate = useNavigate()
  const { hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules } = useAuth()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const visibility = { isPlatformAdmin, activePersona, hasNavPermission, modules }

  const navItems = useMemo(
    () => flattenVisibleNavItems(NAV_SECTIONS, hasPermission, visibility),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibility fields listed
    [hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules],
  )

  const rows: PaletteRow[] = useMemo(() => {
    const q = query.trim().toLowerCase()
    const match = (label: string, group?: string) => {
      if (!q) return true
      const tokens = q.split(/\s+/).filter(Boolean)
      const words = `${group ?? ''} ${label}`
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
      return tokens.every((token) => words.some((w) => w.startsWith(token) || w === token))
    }

    const navRows: PaletteRow[] = navItems
      .filter((item) => match(item.label, item.group))
      .map((item) => ({ kind: 'nav', item }))

    const actions: PaletteRow[] = []
    if (onChooseCompanyRole && match('Choose company and role')) {
      actions.push({
        kind: 'action',
        id: 'entry',
        label: 'Choose company and role…',
        run: () => {
          onOpenChange(false)
          onChooseCompanyRole()
        },
      })
    }
    if (onToggleTheme && match('Toggle theme')) {
      actions.push({
        kind: 'action',
        id: 'theme',
        label: 'Toggle theme',
        run: () => {
          onToggleTheme()
          onOpenChange(false)
        },
      })
    }

    const helpRows: PaletteRow[] = (helpArticles ?? [])
      .filter((article) => article.title && match(article.title, HELP_GROUP))
      .map((article) => ({ kind: 'help', slug: article.slug, title: article.title }))

    return [...navRows, ...helpRows, ...actions]
  }, [navItems, query, onChooseCompanyRole, onToggleTheme, onOpenChange, helpArticles])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActiveIndex(0)
      return
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onOpenChange(false)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, Math.max(rows.length - 1, 0)))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' && rows[activeIndex]) {
        e.preventDefault()
        runRow(rows[activeIndex])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, rows, activeIndex, onOpenChange])

  const runRow = (row: PaletteRow) => {
    if (row.kind === 'nav') {
      onOpenChange(false)
      navigate(row.item.to)
      return
    }
    if (row.kind === 'help') {
      onOpenChange(false)
      navigate(helpPath(row.slug))
      return
    }
    row.run()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-lg rounded-2xl border border-border bg-surface shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search size={16} className="text-foreground-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to page…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-foreground-muted outline-none"
            aria-label="Search pages"
          />
          <kbd className="hidden sm:inline text-[10px] font-mono text-foreground-muted border border-border rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>
        <ul className="max-h-72 overflow-y-auto py-1.5" role="listbox">
          {rows.length === 0 ? (
            <li className="px-4 py-6 text-center text-xs text-foreground-muted">No matches</li>
          ) : (
            rows.map((row, index) => {
              const selected = index === activeIndex
              const label =
                row.kind === 'nav'
                  ? row.item.group
                    ? `${row.item.group} → ${row.item.label}`
                    : row.item.label
                  : row.kind === 'help'
                    ? `${HELP_GROUP} → ${row.title}`
                    : row.label
              const Icon = row.kind === 'nav' ? row.item.icon : row.kind === 'help' ? CircleHelp : Search
              const key =
                row.kind === 'nav' ? `${row.item.to}:${row.item.label}` : row.kind === 'help' ? `help:${row.slug}` : row.id
              return (
                <li key={key} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                      selected ? 'bg-accent/15 text-accent' : 'text-foreground hover:bg-surface-hover'
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => runRow(row)}
                  >
                    <Icon size={15} className="shrink-0 opacity-70" />
                    <span className="truncate">{label}</span>
                  </button>
                </li>
              )
            })
          )}
        </ul>
      </div>
    </div>
  )
}

/** Global Meta/Ctrl+K listener — mount once in Shell. */
export function useCommandPaletteHotkey(onOpen: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpen()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onOpen])
}
